import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Chapter } from "@/components/Chapters";
import { scrollToSection } from "@/components/Navbar";
import { useLang } from "@/i18n";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const reveal = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Slider = ({ label, display, min, max, step, value, onChange, testid }) => (
  <div>
    <div className="flex justify-between items-baseline mb-5">
      <span className="text-[10px] tracking-[0.3em] uppercase text-bone/40">{label}</span>
      <span className="font-display text-xl sm:text-2xl text-bone">{display}</span>
    </div>
    <input
      data-testid={testid}
      type="range"
      className="calc-range w-full"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </div>
);

export default function Calculator() {
  const { t } = useLang();
  const [price, setPrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);
  const [taxPct, setTaxPct] = useState(0.6);
  const [insurance, setInsurance] = useState(1800);

  const calc = useMemo(() => {
    const down = (price * downPct) / 100;
    const loan = price - down;
    const r = rate / 100 / 12;
    const n = term * 12;
    const pi = r > 0 ? (loan * r) / (1 - Math.pow(1 + r, -n)) : loan / n;
    const tax = (price * taxPct) / 100 / 12;
    const ins = insurance / 12;
    return {
      down,
      loan,
      pi,
      tax,
      ins,
      monthly: pi + tax + ins,
      totalInterest: pi * n - loan,
    };
  }, [price, downPct, rate, term, taxPct, insurance]);

  const getExactRates = () => {
    const detail = {
      interest: "Buying a home",
      message: t.calc.prefill
        .replace("{price}", fmt(price))
        .replace("{down}", String(downPct))
        .replace("{rate}", String(rate))
        .replace("{term}", String(term))
        .replace("{monthly}", fmt(calc.monthly)),
    };
    window.dispatchEvent(new CustomEvent("opeam:prefill", { detail }));
    scrollToSection("#contact");
  };

  return (
    <Chapter id="calculator" number="07" eyebrow={t.calc.eyebrow} testid="chapter-calculator">
      <motion.h2
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
      >
        {t.calc.h2a} <span className="italic text-copper">{t.calc.h2b}</span>
      </motion.h2>
      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="mt-8 max-w-xl text-base leading-relaxed text-bone/60"
      >
        {t.calc.intro}
      </motion.p>

      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10%" }}
        className="mt-14 grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10"
      >
        <div className="bg-ink p-10 sm:p-14 space-y-10">
          <Slider
            label={t.calc.price}
            display={fmt(price)}
            min={100000}
            max={2000000}
            step={5000}
            value={price}
            onChange={setPrice}
            testid="calc-price-slider"
          />
          <Slider
            label={t.calc.down}
            display={`${downPct}% · ${fmt(calc.down)}`}
            min={0}
            max={50}
            step={1}
            value={downPct}
            onChange={setDownPct}
            testid="calc-down-slider"
          />
          <Slider
            label={t.calc.rate}
            display={`${rate.toFixed(3).replace(/0+$/, "").replace(/\.$/, ".0")}%`}
            min={3}
            max={10}
            step={0.125}
            value={rate}
            onChange={setRate}
            testid="calc-rate-slider"
          />
          <Slider
            label={t.calc.tax}
            display={`${taxPct.toFixed(2)}% · ${fmt((price * taxPct) / 100)}`}
            min={0}
            max={2}
            step={0.05}
            value={taxPct}
            onChange={setTaxPct}
            testid="calc-tax-slider"
          />
          <Slider
            label={t.calc.insurance}
            display={fmt(insurance)}
            min={500}
            max={5000}
            step={100}
            value={insurance}
            onChange={setInsurance}
            testid="calc-insurance-slider"
          />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-bone/40 mb-5">{t.calc.term}</p>
            <div className="flex gap-2">
              {[30, 20, 15].map((yr) => (
                <button
                  key={yr}
                  data-testid={`calc-term-${yr}`}
                  onClick={() => setTerm(yr)}
                  className={`flex-1 py-3.5 text-sm border transition-colors duration-300 ${
                    term === yr
                      ? "border-copper text-copper"
                      : "border-white/15 text-bone/50 hover:border-white/40 hover:text-bone"
                  }`}
                >
                  {yr} {t.calc.yr}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-ink p-10 sm:p-14 flex flex-col justify-between gap-12">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-bone/40">
              {t.calc.resultTitle}
            </p>
            <div className="mt-4 overflow-hidden">
              <motion.span
                key={Math.round(calc.monthly)}
                data-testid="calc-monthly-payment"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="block font-display text-6xl sm:text-7xl tracking-tight text-copper"
              >
                {fmt(calc.monthly)}
              </motion.span>
            </div>
            <p className="mt-3 text-xs text-bone/35 leading-relaxed">
              {t.calc.disclaimer}
            </p>
          </div>

          <div className="divide-y divide-white/10 border-y border-white/10 text-sm">
            {[
              { label: t.calc.rowPi, value: `${fmt(calc.pi)} /mo`, testid: "calc-breakdown-pi" },
              { label: t.calc.rowTax, value: `${fmt(calc.tax)} /mo`, testid: "calc-breakdown-tax" },
              { label: t.calc.rowIns, value: `${fmt(calc.ins)} /mo`, testid: "calc-breakdown-insurance" },
              { label: t.calc.rowDown, value: fmt(calc.down), testid: "calc-breakdown-down" },
              { label: t.calc.rowLoan, value: fmt(calc.loan), testid: "calc-breakdown-loan" },
            ].map((r) => (
              <div key={r.testid} data-testid={r.testid} className="flex justify-between py-3.5">
                <span className="text-bone/40">{r.label}</span>
                <span className="text-bone/90">{r.value}</span>
              </div>
            ))}
          </div>

          <button
            data-testid="calc-cta-button"
            onClick={getExactRates}
            className="btn-swipe group flex items-center justify-center gap-3 border border-copper text-copper px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-300 w-full"
          >
            {t.calc.cta}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </motion.div>
    </Chapter>
  );
}
